import { useState, useEffect } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../db/firebase";
import { Link } from "react-router-dom";

const CategorySections = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch categories from Firebase
  const fetchCategories = async () => {
    try {
      setLoading(true);
      const querySnapshot = await getDocs(collection(db, "categories"));
      const categoriesData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Filter only main categories
      const mainCategories = categoriesData.filter(cat => 
        !cat.ParentCategory || cat.CategoryType === "main"
      );

      // Define the desired order
      const desiredOrder = [
        "executive men shoes",
        "regular men shoe", 
        "women shoes"
      ];

      // Sort categories based on desired order
      const sortedCategories = mainCategories.sort((a, b) => {
        const aIndex = desiredOrder.findIndex(name => 
          a.CategoryName.toLowerCase().includes(name.toLowerCase())
        );
        const bIndex = desiredOrder.findIndex(name => 
          b.CategoryName.toLowerCase().includes(name.toLowerCase())
        );
        
        // If both categories are in the desired order, sort by that order
        if (aIndex !== -1 && bIndex !== -1) {
          return aIndex - bIndex;
        }
        // If only A is in desired order, it comes first
        if (aIndex !== -1) return -1;
        // If only B is in desired order, it comes first  
        if (bIndex !== -1) return 1;
        // If neither is in desired order, maintain original order
        return 0;
      });

      setCategories(sortedCategories);
    } catch (err) {
      console.error("Error fetching categories:", err);
      setError("Failed to load categories");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  // Main Category Banner Component
  const MainCategoryBanner = ({ category, index }) => {
    const [imageLoaded, setImageLoaded] = useState(false);
    const [imageError, setImageError] = useState(false);

    const isEven = index % 2 === 0;

    return (
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          {category.CatImgUrl && !imageError ? (
            <>
              <img
                src={category.CatImgUrl}
                alt={category.CategoryName}
                className={`w-full h-full object-cover transition duration-1000 ${
                  imageLoaded ? 'opacity-100' : 'opacity-0'
                }`}
                onLoad={() => setImageLoaded(true)}
                onError={() => setImageError(true)}
              />
              {!imageLoaded && (
                <div className="absolute inset-0 bg-taupe animate-pulse flex items-center justify-center">
                  <div className="text-beige text-lg">Loading...</div>
                </div>
              )}
            </>
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-taupe to-darktaupe flex items-center justify-center">
              <div className="text-center text-beige">
                <svg className="w-24 h-24 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <p className="text-lg font-medium">No Image Available</p>
              </div>
            </div>
          )}
          
          {/* Dark Overlay for better text readability */}
          <div className="absolute inset-0 bg-charcoal/50"></div>
        </div>

        {/* Content */}
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 w-full">
          <div className={`flex flex-col ${isEven ? 'items-start text-left' : 'items-end text-right'}`}>
            {/* Category Name */}
            <h2 className="text-5xl lg:text-7xl font-bold text-offwhite mb-8 leading-tight">
              {category.CategoryName}
            </h2>

            {/* Shop Now Button - Main category goes to category page */}
            <Link
              to={`/shop/category/${category.id}`}
              className="bg-tan text-charcoal px-10 py-5 rounded-lg font-bold text-xl hover:bg-beige transition duration-300 transform hover:scale-105 inline-flex items-center gap-4 border-2 border-tan hover:border-beige"
            >
              Shop Collection
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </Link>
          </div>
        </div>
      </section>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-offwhite flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-darktaupe text-lg">Loading categories...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-offwhite flex items-center justify-center">
        <div className="text-center">
          <div className="text-redburnt text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-darktaupe mb-2">Error Loading Categories</h2>
          <p className="text-taupe mb-4">{error}</p>
          <button
            onClick={fetchCategories}
            className="bg-primary text-offwhite px-6 py-2 rounded-lg hover:bg-darktaupe transition"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-offwhite">
      {/* Header Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center bg-gradient-to-br from-primary to-deepgreen">
        <div className="text-center text-offwhite max-w-4xl mx-auto px-6">
          <h1 className="text-6xl lg:text-8xl font-bold mb-6 tracking-tight">
            READY TO WEAR
          </h1>
          <p className="text-xl lg:text-2xl text-beige mb-8 leading-relaxed">
            Discover our curated collection of premium fashion items, carefully selected to elevate your style and comfort.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link
              to="/shop"
              className="bg-tan text-charcoal px-8 py-4 rounded-lg font-bold text-lg hover:bg-beige transition duration-300 transform hover:scale-105 border-2 border-tan hover:border-beige"
            >
              Shop All Products
            </Link>
            <Link
              to="/shop/category/all"
              className="bg-offwhite/20 text-offwhite px-8 py-4 rounded-lg font-bold text-lg hover:bg-offwhite/30 transition duration-300 backdrop-blur-sm border border-offwhite/30"
            >
              Browse Categories
            </Link>
          </div>
        </div>
      </section>

      {/* Main Category Banners Only */}
      {categories.map((category, index) => (
        <MainCategoryBanner 
          key={`main-${category.id}`}
          category={category} 
          index={index}
        />
      ))}

      {/* Explore More Section */}
      <section className="relative min-h-[50vh] flex items-center justify-center bg-darktaupe">
        <div className="text-center text-offwhite max-w-4xl mx-auto px-6">
          <h2 className="text-4xl lg:text-5xl font-bold mb-6">
            Explore More
          </h2>
          <p className="text-xl text-beige mb-8 max-w-2xl mx-auto">
            Discover our complete collection and find your perfect style.
          </p>
          <Link
            to="/shop"
            className="inline-flex items-center justify-center px-10 py-4 bg-tan text-charcoal font-bold text-lg rounded-lg hover:bg-beige transition duration-300 transform hover:scale-105 border-2 border-tan hover:border-beige"
          >
            View All Collections
            <svg 
              className="ml-3 w-5 h-5" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M17 8l4 4m0 0l-4 4m4-4H3" 
              />
            </svg>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default CategorySections;